import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, UserRound, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MemberCard({ m }) {
  return (
    <div
      className="w-[240px] shrink-0 snap-start border border-[#27272A] bg-[#121214] group hover:border-[#C5A059]/40 transition-colors duration-500"
      data-testid={`staff-card-${m.id.slice(0, 8)}`}
    >
      <div className="aspect-[3/4] overflow-hidden bg-[#0A0A0C] flex items-center justify-center">
        {m.photo ? (
          <img src={m.photo} alt={m.nom} loading="lazy" draggable={false} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700" />
        ) : (
          <UserRound size={56} className="text-[#27272A]" strokeWidth={1} />
        )}
      </div>
      <div className="p-5">
        <p className="font-display text-base uppercase font-bold leading-tight">{m.nom}</p>
        <p className="text-[#C5A059] text-[10px] uppercase tracking-[0.25em] mt-2">{m.poste}</p>
      </div>
    </div>
  );
}

function StaffRow({ label, members, testid, labelTestid }) {
  const rowRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, scrollStart: 0 });
  const scroll = (dir) => rowRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" });

  const onPointerDown = (e) => {
    drag.current = { down: true, startX: e.clientX, scrollStart: rowRef.current.scrollLeft };
    rowRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    rowRef.current.scrollLeft = drag.current.scrollStart - (e.clientX - drag.current.startX);
  };
  const endDrag = () => { drag.current.down = false; };

  return (
    <div className="mt-16">
      <div className="flex items-center justify-between mb-8">
        <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]" data-testid={labelTestid}>{label} · {members.length}</p>
        {members.length > 2 && (
          <div className="hidden sm:flex gap-2">
            <button onClick={() => scroll(-1)} data-testid={`${testid}-scroll-left`} aria-label="Défiler à gauche" className="w-9 h-9 border border-[#27272A] flex items-center justify-center text-[#71717A] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"><ChevronLeft size={16} /></button>
            <button onClick={() => scroll(1)} data-testid={`${testid}-scroll-right`} aria-label="Défiler à droite" className="w-9 h-9 border border-[#27272A] flex items-center justify-center text-[#71717A] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>
      {members.length === 0 ? (
        <p className="text-[#3f3f46] text-sm border border-[#27272A] p-10 text-center" data-testid={testid}>—</p>
      ) : (
        <div
          ref={rowRef}
          data-testid={testid}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 cursor-grab active:cursor-grabbing select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [touch-action:pan-y]"
        >
          {members.map((m) => <MemberCard key={m.id} m={m} />)}
        </div>
      )}
    </div>
  );
}

export default function EquipePage() {
  const [members, setMembers] = useState([]);
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/staff`).then((r) => setMembers(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const dirigeants = members.filter((m) => m.categorie === "dirigeant");
  const employes = members.filter((m) => m.categorie === "employe");

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white grain">
      <header className="border-b border-[#27272A] bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="equipe-logo-home"><img src="/assets/logo-dark-sm.png" alt="D2R" className="h-9 w-auto" /></Link>
          <Link to="/" data-testid="equipe-back-link" className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#C5A059] transition-colors uppercase tracking-[0.2em]">
            <ArrowLeft size={14} /> {t("eventpage.back")}
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28" data-testid="equipe-page">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("staff.eyebrow")}</p>
          <h1 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tight leading-[1.02]">
            {t("staff.title.a")} <span className="text-[#C5A059]">{t("staff.title.b")}</span>
          </h1>
        </motion.div>

        {members.length === 0 ? (
          <p className="text-[#71717A] text-sm mt-16 border border-[#27272A] p-14 text-center" data-testid="staff-empty">{t("staff.empty")}</p>
        ) : (
          <>
            <StaffRow label={t("staff.dir")} members={dirigeants} testid="staff-dirigeants" labelTestid="staff-dir-label" />
            <StaffRow label={t("staff.emp")} members={employes} testid="staff-employes" labelTestid="staff-emp-label" />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
